/** O perfil do LinkedIn como a IA leu: o que mudou desde a última leitura aplicada, a URL e o período. */
import { parseLinkedInUrl } from "@/utils/external-url";
import type { LinkedInPainel } from "@/types/freelancer-painel";
import type { DiffSummary, ExperienceItem, FormacaoItem, LinkedInExtracted } from "@/types/linkedin";

export type MarcaDaLeitura = "novo" | "mudou";

export const CAMPOS_DO_PERFIL = ["headline", "resumo", "localizacao"] as const;
export type CampoDoPerfil = (typeof CAMPOS_DO_PERFIL)[number];

export interface MarcasDaLeitura {
  campos: Set<CampoDoPerfil>;
  experiencias: Map<string, MarcaDaLeitura>;
  formacoes: Set<string>;
  skills: Set<string>;
}

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

export function chaveExperiencia(experiencia: ExperienceItem): string {
  return [experiencia.empresa_nome, experiencia.cargo_nome, experiencia.data_inicio].join("|").toLowerCase();
}

export function chaveFormacao(formacao: FormacaoItem): string {
  return [formacao.instituicao, formacao.curso ?? ""].join("|").toLowerCase();
}

export function chaveSkill(nome: string): string {
  return nome.trim().toLowerCase();
}

function campoDoPerfil(campo: string): CampoDoPerfil | null {
  return (CAMPOS_DO_PERFIL as readonly string[]).includes(campo) ? (campo as CampoDoPerfil) : null;
}

/**
 * O que marcar como novo ou mudado no perfil. Null quando não há com o que
 * comparar (primeira leitura): marcar tudo como novo só faria barulho.
 */
export function marcasDoDiff(diff: DiffSummary | null | undefined): MarcasDaLeitura | null {
  if (!diff || diff.is_first) return null;
  const experiencias = new Map<string, MarcaDaLeitura>();
  for (const experiencia of diff.added.experiencias) experiencias.set(chaveExperiencia(experiencia), "novo");
  for (const mudanca of diff.changed) {
    const chave = chaveExperiencia(mudanca.experiencia);
    if (!experiencias.has(chave) && mudanca.fields.length > 0) experiencias.set(chave, "mudou");
  }
  const campos = new Set<CampoDoPerfil>();
  for (const mudanca of diff.fields_changed) {
    const campo = campoDoPerfil(mudanca.field);
    if (campo) campos.add(campo);
  }
  return {
    campos,
    experiencias,
    formacoes: new Set(diff.added.formacao.map(chaveFormacao)),
    skills: new Set(diff.added.skills.map((skill) => chaveSkill(skill.nome))),
  };
}

/** A primeira URL que é mesmo um perfil do LinkedIn, na ordem de preferência. */
export function urlDoPerfil(...candidatas: (string | null | undefined)[]): string | null {
  for (const candidata of candidatas) {
    const parsed = parseLinkedInUrl(candidata);
    if (parsed.status === "ok" && parsed.href && !parsed.warning) return parsed.href;
  }
  return null;
}

function mesAno(valor: string): { ano: number; mes: number | null } | null {
  const match = valor.match(/^(\d{4})(?:-(\d{2}))?/);
  if (!match) return null;
  return { ano: Number(match[1]), mes: match[2] ? Number(match[2]) : null };
}

function dataParcial(valor: string): string {
  const data = mesAno(valor);
  if (!data) return valor;
  return data.mes ? `${MESES[data.mes - 1]}/${data.ano}` : String(data.ano);
}

function duracao(meses: number): string | null {
  if (meses <= 0) return null;
  const anos = Math.floor(meses / 12);
  const resto = meses % 12;
  const partes = [anos > 0 && `${anos} ${anos === 1 ? "ano" : "anos"}`, resto > 0 && `${resto} ${resto === 1 ? "mês" : "meses"}`];
  return partes.filter(Boolean).join(" e ");
}

/** "set/2017 até hoje · 3 anos e 2 meses", como o LinkedIn mostra o período. */
export function periodoDaExperiencia(inicio: string | null, fim: string | null, atual: boolean, hoje: Date = new Date()): string | null {
  if (!inicio) return null;
  const comeco = mesAno(inicio);
  const termino = atual ? { ano: hoje.getFullYear(), mes: hoje.getMonth() + 1 } : fim ? mesAno(fim) : null;
  const texto = `${dataParcial(inicio)} ${atual ? "até hoje" : fim ? `a ${dataParcial(fim)}` : ""}`.trim();
  if (!comeco || !termino) return texto;
  const meses = (termino.ano - comeco.ano) * 12 + ((termino.mes ?? 1) - (comeco.mes ?? 1)) + 1;
  const tempo = duracao(meses);
  return tempo ? `${texto} · ${tempo}` : texto;
}

const SKILLS_NO_RESUMO = 12;

/** A leitura traz "2023" ou "2023-03"; o painel, como o banco, usa data completa. */
function dataCompleta(valor: string | null): string | null {
  const data = valor ? mesAno(valor) : null;
  if (!data) return null;
  return `${data.ano}-${String(data.mes ?? 1).padStart(2, "0")}-01`;
}

/**
 * A leitura em revisão no formato do resumo do painel, para quem ainda não tem
 * LinkedIn aplicado: as competências principais primeiro, as outras completam.
 */
export function painelDaLeitura(perfil: LinkedInExtracted, lidoEm: string | null): LinkedInPainel {
  const atual = perfil.experiencias.find((experiencia) => experiencia.is_atual) ?? null;
  const skills = [...perfil.skills].sort((left, right) => Number(right.is_principal) - Number(left.is_principal));
  return {
    url_status: "ok",
    headline: perfil.headline,
    cargo_atual: atual?.cargo_nome ?? null,
    empresa_atual: atual?.empresa_nome ?? null,
    senioridade: perfil.senioridade_global_inferida,
    anos_experiencia: perfil.anos_experiencia_total,
    aberto_a_oportunidades: null,
    atualizado_em: lidoEm,
    resumo: perfil.resumo,
    experiencias: perfil.experiencias.map((experiencia) => ({
      empresa: experiencia.empresa_nome,
      cargo: experiencia.cargo_nome,
      inicio: dataCompleta(experiencia.data_inicio),
      fim: dataCompleta(experiencia.data_fim),
      atual: experiencia.is_atual,
    })),
    skills_principais: skills.slice(0, SKILLS_NO_RESUMO).map((skill) => skill.nome),
  };
}

/** O painel só tem o LinkedIn aplicado; vazio, a revisão mostra a leitura. */
export function linkedinVazio(linkedin: LinkedInPainel | undefined): boolean {
  return !linkedin || (!linkedin.headline && !linkedin.cargo_atual && linkedin.experiencias.length === 0);
}
